import React, { useEffect, useState } from "react";

import Select from "react-select";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import MaterialsAPI from "../../API/materials.api";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import MaterialSchemas from "../../schemas/material.schema";
import LookupsAPI from "../../API/lookups.api";
import { trimValues } from "../../utils/Helpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import BaseModal from "./BaseModal";
import EditableImage from "../../components/Images/EditableImages";

import SimpleNotification from "../../components/Notifications/SimpleNotification";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const MaterialModal = ({
    modalButton,
    modalButtonSetter,
    completionHandler,
    data,
    index,
    groupsLookup,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [material, setMaterial] = useState({});
    const [materialGroups, setMaterialGroups] = useState([]);
    const [defaultMaterialGroup, setDefaultMaterialGroup] = useState({});

    const { t } = useTranslation();

    useEffect(() => {
        if (isEmpty(groupsLookup)) {
            fetchMaterialGroups();
        } else {
            mapMaterialGroups();
        }
    }, [groupsLookup]);

    useEffect(() => {
        if (modalButton) {
            openModal();
            setMaterialData(data);
            modalButtonSetter(false);
        }
    }, [modalButton]);

    useEffect(() => {
        validate(
            material,
            MaterialSchemas.CreateAndEditMaterialSchema,
            setIsFormValid,
            setError
        );
    }, [material]);

    const setMaterialData = (data) => {
        if (data) {
            setDefaultMaterialGroup({
                label: data?.materialGroupName,
                value: data?.materialGroupId,
            });

            setMaterial({
                name: data?.name,
                technicalName: data?.technicalName,
                description: data?.description,
                materialGroupId: data?.materialGroupId,
                image: data?.image,
                systemGenerated: data?.systemGenerated,
            });
        } else {
            setMaterial({});
        }
    };

    const fetchMaterialGroups = async () => {
        try {
            const result = await LookupsAPI.getMaterialGroups();
            const mappedToSelect = result.map((group) => {
                return { label: group?.name, value: group?.id };
            });
            setMaterialGroups(mappedToSelect);
        } catch (responseError) {
            ErrorAlert(t("unhandledException"));
        }
    };

    const mapMaterialGroups = () => {
        const mappedGroups = groupsLookup.map((group) => {
            return { value: group?.id, label: group?.name };
        });
        setMaterialGroups(mappedGroups);
    };

    const openModal = () => {
        setIsVisible(true);
    };

    const closeModal = () => {
        setDefaultMaterialGroup({});
        setIsVisible(false);
        setIsSubmitting(false);
        setIsFormSubmitted(false);
        setIsFormValid(false);
        setMaterial({});
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const newMaterial = trimValues(material);
        if (index !== undefined && index !== null) {
            try {
                const result = await MaterialsAPI.updateMaterial({
                    ...newMaterial,
                    id: data?.id,
                });

                if (!isEmpty(result)) {
                    SuccessAlert(t("editMaterialSuccessMessage"));
                    LogCapture.info(
                        `Material updated with name: ${newMaterial?.name} & id: ${data?.id}`,
                        ENTITIES.materials,
                        ACTIONS.materials.update
                    );
                    completionHandler(false, newMaterial, index);
                    closeModal();
                }
                setIsSubmitting(false);
            } catch (error) {
                ErrorAlert(t("editMaterialFailMessage"));
                LogCapture.error(
                    `Couldn't update material with name: ${newMaterial?.name} & id: ${data?.id}, error: ${error}`
                );
            }
        } else {
            try {
                const result = await MaterialsAPI.addMaterial({
                    ...newMaterial,
                });

                if (!isEmpty(result)) {
                    SuccessAlert(t("createdMaterialSuccessMessage"));
                    LogCapture.info(
                        `Material created with name: ${newMaterial?.name}`,
                        ENTITIES.materials,
                        ACTIONS.materials.create
                    );
                    completionHandler(true);
                    closeModal();
                }
            } catch (error) {
                ErrorAlert(t("createMaterialFailMessage"));
                LogCapture.error(
                    `Couldn't create material with name: ${newMaterial?.name}, error: ${error}`
                );
            }
        }
        setIsSubmitting(false);
    };

    const handleInputChange = (e) => {
        setMaterial({
            ...material,
            [e.target.name]: e.target.value,
        });
    };

    const handleGroupChange = (newValue) => {
        setDefaultMaterialGroup({ ...newValue });
        setMaterial({ ...material, materialGroupId: newValue?.value });
    };

    const fields = [
        {
            name: "name",
            label: (
                <label
                    className={`uppercase required ${
                        material.systemGenerated && "disabled"
                    }`}
                >
                    <Trans i18nKey="name">Name</Trans>
                </label>
            ),
            input: (
                <input
                    id="material-name"
                    disabled={material.systemGenerated}
                    onChange={handleInputChange}
                    value={material?.name || ""}
                    name="name"
                    type="text"
                    placeholder="Ex. Corn Silage"
                    className={`input-element ${
                        material.systemGenerated && "disabled"
                    }`}
                />
            ),
        },
        {
            name: "technicalName",
            label: (
                <label
                    className={`uppercase ${
                        material.systemGenerated && "disabled"
                    }`}
                >
                    <Trans i18nKey="technicalName">Technical Name</Trans>
                </label>
            ),
            input: (
                <input
                    id="material-technical-name"
                    disabled={material.systemGenerated}
                    onChange={handleInputChange}
                    value={material?.technicalName || ""}
                    name="technicalName"
                    type="text"
                    placeholder="Ex. CS123"
                    className={`input-element ${
                        material.systemGenerated && "disabled"
                    }`}
                />
            ),
        },
        {
            name: "materialGroupId",
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="group">Group</Trans>
                </label>
            ),
            input: (
                <Select
                    id="material-group"
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    name="group"
                    placeholder="Select Group..."
                    options={materialGroups}
                    value={
                        isEmpty(defaultMaterialGroup)
                            ? ""
                            : defaultMaterialGroup
                    }
                    onChange={handleGroupChange}
                    theme={getReactSelectTheme}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            name: "description",
            label: (
                <label
                    className={`uppercase ${
                        material.systemGenerated && "disabled"
                    }`}
                >
                    <Trans i18nKey="description">Description</Trans>
                </label>
            ),
            input: (
                <textarea
                    disabled={material.systemGenerated}
                    name="description"
                    value={material?.description || ""}
                    onChange={handleInputChange}
                    placeholder="Type your description here..."
                    className={`input-element textarea-element ${
                        material.systemGenerated && "disabled"
                    }`}
                ></textarea>
            ),
        },
    ];

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div
                        className={`input-group ${
                            error[field.name] && isFormSubmitted
                                ? "invalid-warning-input"
                                : ""
                        }`}
                        key={index}
                    >
                        {field.label}
                        {field.input}
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error[field.name]} />
                        )}
                    </div>
                );
            })
        );
    };

    const renderUploadImage = () => {
        return (
            <EditableImage
                state={material}
                stateSetter={setMaterial}
                disabled={!material.systemGenerated}
                defaultImage={
                    require("../../assets/images/material.png").default
                }
            />
        );
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                title={data ? t("editMaterial") : t("createMaterial")}
                action={{
                    submitAction: handleSubmit,
                    disabled: isSubmitting,
                    name: data ? t("edit") : t("add"),
                }}
                isSmall={true}
            >
                {material.systemGenerated && data && (
                    <SimpleNotification
                        message={t("systemGeneratedMaterialWarning")}
                    />
                )}
                <div className="main-customer-form-inputs">
                    <div className="upload-img-box flex">
                        <div className="relative">{renderUploadImage()}</div>
                        <div className="w-full">{renderFields()}</div>
                    </div>
                </div>
            </BaseModal>
        </>
    );
};

export default MaterialModal;
